import {connect} from 'react-redux';

import {ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {AppState} from '../../store/appState';
import {getComments} from '../../store/data/dataSelectors';
import {forceInternalComposerPopoutAction} from '../../store/panels/viewer/viewerPanelActions';
import {ViewerPanelPagesState, ViewerPanelPageTypesEnum} from '../../store/panels/viewer/viewerPanelState';
import {PopupViewer} from './popupViewer';

/*
 * Props.
 */

interface PopupViewerContainerProps {
  panelId: ViewerPanelId;
  pages: ViewerPanelPagesState;
  composeCommentUid?: string;
}

/*
 * Selectors.
 */

function getPages(_state: AppState, props: PopupViewerContainerProps) {
  return props.pages;
}

function getComposeCommentUid(_state: AppState, props: PopupViewerContainerProps) {
  return props.composeCommentUid;
}

function makeGetComposeComment() {
  return createSelector(
    [getComments, getComposeCommentUid],
    (comments, composeCommentUid) => (composeCommentUid && comments[composeCommentUid]) || undefined,
  );
}

function makeGetIsComposing(getComment: ReturnType<typeof makeGetComposeComment>) {
  return createSelector([getComment, getPages], (comment, pages) =>
    Boolean(comment && pages.current.type === ViewerPanelPageTypesEnum.NO_CONVERSATION),
  );
}

function makeGetShouldConfirmClose(
  getComment: ReturnType<typeof makeGetComposeComment>,
  getIsComposing: ReturnType<typeof makeGetIsComposing>,
) {
  return createSelector([getIsComposing, getComment], (isComposing, comment) => {
    if (!isComposing || !comment) {
      return false;
    }

    const hasTeammates = comment.teammateIds && comment.teammateIds.length > 0;
    const hasAttachments = comment.attachments.length > 0;

    return Boolean(hasTeammates || hasAttachments || comment.topic || comment.text);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PopupViewerContainerProps>()(() => {
  const getComposeComment = makeGetComposeComment();
  const getIsComposing = makeGetIsComposing(getComposeComment);
  return {
    composeComment: getComposeComment,
    isComposing: getIsComposing,
    shouldConfirmClose: makeGetShouldConfirmClose(getComposeComment, getIsComposing),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<PopupViewerContainerProps>()(({panelId}) => ({
  onPopoutClick: () => forceInternalComposerPopoutAction(panelId),
}));

export const PopupViewerContainer = connect(mapStateToProps, mapDispatchToProps)(PopupViewer);
